import React from 'react';
import { CVData } from '../../types';
import { BaseTemplate } from './BaseTemplate';
import { generateTemplateColors } from '../../lib/colorUtils';

interface Template8Props {
  data: CVData;
  pageNumber?: number;
  customColor?: string;
}

export function Template8({ data, pageNumber = 1, customColor }: Template8Props) {
  // Usar el color personalizado si está disponible, o el color por defecto
  const baseColor = customColor || '#696969';
  
  console.log("Template8 - Color personalizado recibido:", customColor);
  console.log("Template8 - Color base utilizado:", baseColor);
  
  // Generar colores derivados basados en el color principal
  const colors = generateTemplateColors(baseColor);
  
  console.log("Template8 - Colores generados:", colors);

  // Verificamos si hay cursos para mostrar
  const hasCourses = data.courses && data.courses.length > 0;
  
  // Verificamos si es una página de continuación
  const isContinuationPage = (data as any).isContinuationPage || false;
  
  // Verificamos si es una página de continuación específica del caso 3
  const isCase3ContinuationPage = (data as any).isCase3ContinuationPage || false;
  
  // Verificamos si es una página de continuación específica del caso 4
  const isCase4ContinuationPage = (data as any).isCase4ContinuationPage || false;
  
  // Verificamos si es la segunda página del caso 5 (3 experiencias)
  const isCase5SecondPage = (data as any).isCase5SecondPage || false;
  
  // Verificamos si es la tercera página del caso 5 (solo cursos)
  const isCase5ThirdPage = (data as any).isCase5ThirdPage || false;
  
  // Verificamos si es la segunda o tercera página del caso 6
  const isCase6SecondPage = (data as any).isCase6SecondPage || false;
  const isCase6ThirdPage = (data as any).isCase6ThirdPage || false;
  
  // Verificamos si es la segunda página del caso 7
  const isCase7SecondPage = (data as any).isCase7SecondPage || false;
  
  // Verificamos si es la tercera página del caso 7
  const isCase7ThirdPage = (data as any).isCase7ThirdPage || false;
  
  // Verificamos si es la segunda, tercera o cuarta página del caso 8
  const isCase8SecondPage = (data as any).isCase8SecondPage || false;
  const isCase8ThirdPage = (data as any).isCase8ThirdPage || false;
  const isCase8FourthPage = (data as any).isCase8FourthPage || false;
  
  // Verificamos si es una página de continuación con experiencia + cursos
  const isExperiencePlusCourses = (data as any).isExperiencePlusCourses || false;
  
  // Verificamos si es una página de continuación que incluye experiencias
  const isContinuationWithExperiences = (data as any).isContinuationWithExperiences || false;
  
  // Verificamos si es una página de sólo cursos
  const isOnlyCoursesPage = isCase5ThirdPage || isCase8FourthPage || (data as any).isOnlyCoursesPage || false;

  // Verificamos si es una página de continuación para cualquier caso
  const isAnyCaseContinuationPage = isCase3ContinuationPage || isCase4ContinuationPage || 
                                   isCase5SecondPage || isCase5ThirdPage || 
                                   isCase6SecondPage || isCase6ThirdPage || 
                                   isCase7SecondPage || isCase7ThirdPage ||
                                   isCase8SecondPage || isCase8ThirdPage || isCase8FourthPage;

  console.log("Template8 - Es continuación:", isContinuationPage);
  console.log("Template8 - Es página de solo cursos:", isOnlyCoursesPage);
  console.log("Template8 - Hay cursos:", hasCourses);
  console.log("Template8 - Longitud de experiencias:", data.experience?.length || 0);
  console.log("Template8 - Longitud de cursos:", data.courses?.length || 0);
  
  // Función para renderizar los cursos
  const renderCourses = () => {
    if (!data.courses || data.courses.length === 0) {
      console.log("Template8 - No hay cursos para renderizar");
      return null;
    }
    
    console.log("Template8 - Renderizando cursos:", data.courses.length);
    return (
      <div className="space-y-4">
        <h3 className="text-xs uppercase tracking-widest text-neutral-400 mb-4 border-b border-neutral-200 pb-1" 
            style={{ color: colors.accentColor }}>
          Cursos y Actividades Adicionales
        </h3>
        <div className="relative">
          {/* Línea vertical que conecta todos los elementos */}
          <div 
            className="absolute left-2 top-0 bottom-0 w-0.5" 
            style={{ backgroundColor: `${colors.accentColor}40` }}
          ></div>
          
          {/* Contenedor para todos los cursos */}
          <div className="space-y-6 ml-10">
            {data.courses.map((course, index) => (
              <div key={`course-${index}`} className="relative">
                {/* Punto en la línea de tiempo */}
                <div 
                  className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                  style={{ 
                    backgroundColor: 'white', 
                    borderColor: colors.accentColor 
                  }}
                ></div>
                
                {/* Contenido del curso */}
                <div className="space-y-1">
                  <div className="text-sm font-medium text-neutral-800">{course.title}</div>
                  <div className="text-sm text-neutral-600">
                    {course.institution} | {course.period}
                  </div>
                  {course.description && (
                    <p className="text-sm text-neutral-500 mt-1">{course.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Si es una página solo de cursos, solo mostramos los cursos
  if (isOnlyCoursesPage && hasCourses) {
    console.log("Template8 - Renderizando SOLO cursos sin otro contenido");
    return (
      <BaseTemplate
        data={data}
        colors={colors}
        isTemplate1={false}
        renderMainContent={() => (
          <div className="space-y-8 pt-4">
            {renderCourses()}
          </div>
        )}
      />
    );
  }
  
  // Si es una página de continuación con experiencias
  if (isAnyCaseContinuationPage && data.experience && data.experience.length > 0) {
    console.log("Template8 - Renderizando continuación con experiencia");
    
    // Determinar si esta página debe mostrar cursos
    const shouldShowCourses = 
      hasCourses && 
      (isCase3ContinuationPage || isCase4ContinuationPage || isCase6ThirdPage || isCase7ThirdPage) && 
      !(isCase5SecondPage || isCase6SecondPage || isCase7SecondPage || 
        isCase8SecondPage || isCase8ThirdPage);
    
    return (
      <BaseTemplate
        data={data}
        colors={colors}
        isTemplate1={false}
        renderMainContent={(experienceItems) => (
          <div className="space-y-8 pt-4">
            {/* Experiencia continuación */}
            {experienceItems && React.Children.count(experienceItems) > 0 && (
              <div className="space-y-4">
                <h3 className="text-xs uppercase tracking-widest text-neutral-400 mb-4 border-b border-neutral-200 pb-1"
                    style={{ color: colors.accentColor }}>
                  Experiencia Profesional (continuación)
                </h3>
                <div className="relative">
                  {/* Línea vertical que conecta todos los elementos */}
                  <div 
                    className="absolute left-2 top-0 bottom-0 w-0.5" 
                    style={{ backgroundColor: `${colors.accentColor}40` }}
                  ></div>
                  
                  {/* Contenedor para todas las experiencias */}
                  <div className="space-y-8 ml-10">
                    {React.Children.map(experienceItems, (item, index) => {
                      // Simplemente mostramos el elemento sin modificaciones
                      return (
                        <div key={`exp-${index}`} className="relative">
                          {/* Punto en la línea de tiempo */}
                          <div 
                            className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                            style={{ 
                              backgroundColor: 'white', 
                              borderColor: colors.accentColor 
                            }}
                          ></div>
                          {item}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Cursos y Actividades (para casos específicos) */}
            {shouldShowCourses && renderCourses()}
          </div>
        )}
      />
    );
  }

  // Optimizar datos para que la sección de idiomas tenga un tamaño más pequeño
  const optimizedData = {
    ...data,
    languages: data.languages.map(lang => ({
      ...lang,
      // Añadimos una clase especial para el tamaño reducido que será procesada por BaseTemplate
      _smaller: true
    }))
  };

  return (
    <BaseTemplate
      data={optimizedData}
      colors={colors}
      isTemplate1={false}
      renderMainContent={(experienceItems, pageNumber) => (
        <div className="space-y-8">
          {pageNumber === 1 ? (
            <>
              {/* Minimal Header */}
              <div className="border-b-2 border-neutral-200 pb-6 mb-8" 
                  style={{ borderColor: `${colors.accentColor}30` }}>
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-4xl font-light tracking-tight text-neutral-900 mb-2">
                      {data.name}
                    </h1>
                    <h2 className="text-xl font-bold tracking-wide text-neutral-600"
                        style={{ color: colors.accentColor }}>
                      {data.title}
                    </h2>
                  </div>
                </div>
              </div>

              {/* Main Grid */}
              <div className="grid grid-cols-12 gap-10">
                {/* Left Column - This will be handled by BaseTemplate sidebar */}
                
                {/* Right Column - Experience & Education */}
                <div className="col-span-12">
                  {/* Educación Section */}
                  <div className="mb-10">
                    <h3 className="text-xs uppercase tracking-widest text-neutral-400 mb-4 border-b border-neutral-200 pb-1"
                        style={{ color: colors.accentColor }}>
                      Educación
                    </h3>
                    <div className="relative">
                      {/* Línea vertical que conecta todos los elementos */}
                      <div 
                        className="absolute left-2 top-0 bottom-0 w-0.5" 
                        style={{ backgroundColor: `${colors.accentColor}40` }}
                      ></div>
                      
                      {/* Contenedor para todos los elementos de educación */}
                      <div className="space-y-6 ml-10">
                        {/* Ordenamos la educación de más reciente a más antigua */}
                        {[...data.education].reverse().map((edu, index) => (
                          <div key={`edu-${index}`} className="relative">
                            {/* Punto en la línea de tiempo */}
                            <div 
                              className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                              style={{ 
                                backgroundColor: 'white', 
                                borderColor: colors.accentColor 
                              }}
                            ></div>
                            
                            {/* Contenido de educación */}
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-neutral-800">{edu.degree}</div>
                              <div className="text-sm text-neutral-600">{edu.institution}</div>
                              <div className="text-xs text-neutral-500">{edu.period}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Experience Section */}
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-neutral-400 mb-6 border-b border-neutral-200 pb-1"
                        style={{ color: colors.accentColor }}>
                      Experiencia Profesional
                    </h3>
                    <div className="relative">
                      {/* Línea vertical que conecta todos los elementos */}
                      <div 
                        className="absolute left-2 top-0 bottom-0 w-0.5" 
                        style={{ backgroundColor: `${colors.accentColor}40` }}
                      ></div>
                      
                      {/* Contenedor para todas las experiencias */}
                      <div className="space-y-8 ml-10">
                        {React.Children.map(experienceItems, (item, index) => {
                          // Simplemente mostramos el elemento sin modificaciones
                          return (
                            <div key={`exp-${index}`} className="relative">
                              {/* Punto en la línea de tiempo */}
                              <div 
                                className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                                style={{ 
                                  backgroundColor: 'white', 
                                  borderColor: colors.accentColor 
                                }}
                              ></div>
                              {item}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-4 pt-4">
                {/* Experiencia laboral (continuación) - Solo si hay elementos */}
                {experienceItems && React.Children.count(experienceItems) > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-xs uppercase tracking-widest text-neutral-400 mb-6 border-b border-neutral-200 pb-1"
                        style={{ color: colors.accentColor }}>
                      Experiencia Profesional (continuación)
                    </h3>
                    <div className="relative">
                      {/* Línea vertical que conecta todos los elementos */}
                      <div 
                        className="absolute left-2 top-0 bottom-0 w-0.5" 
                        style={{ backgroundColor: `${colors.accentColor}40` }}
                      ></div>
                      
                      {/* Contenedor para todas las experiencias */}
                      <div className="space-y-8 ml-10">
                        {React.Children.map(experienceItems, (item, index) => {
                          // Simplemente mostramos el elemento sin modificaciones
                          return (
                            <div key={`exp-cont-${index}`} className="relative">
                              {/* Punto en la línea de tiempo */}
                              <div 
                                className="absolute -left-10 top-1.5 w-4 h-4 rounded-full border-2"
                                style={{ 
                                  backgroundColor: 'white', 
                                  borderColor: colors.accentColor 
                                }}
                              ></div>
                              {item}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Cursos y Actividades Adicionales */}
                {hasCourses && !isCase5SecondPage && !isCase6SecondPage && !isCase7SecondPage && 
                !isCase8SecondPage && !isCase8ThirdPage && renderCourses()}
              </div>
            </>
          )}
        </div>
      )}
    />
  );
}